import { useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { register } from "ol/proj/proj4";
import { default as OLMap } from "ol/Map";
import MousePosition from "ol/control/MousePosition.js";
import View from "ol/View";
import Zoom from "ol/control/Zoom.js";
import "ol/ol.css";
import TileLayer from "ol/layer/Tile";
import OSM from "ol/source/OSM";
import proj4 from "proj4";
import VectorSource from "ol/source/Vector";
import VectorLayer from "ol/layer/Vector";
import store from "../../stores/store";
import {
  addMapLayer,
  getFeatureInfo,
  getLayerById,
  getMapCenterProjected,
  getMapProjection,
  getZoomLevel,
  mapRemoveControl,
  mapScaleLine,
  setLayerVisibility,
  sortMapLayers,
} from "./mapUtils";
import { cloneDeep } from "lodash";
import { Projection } from "ol/proj";
import {
  MapViewConfig,
  copyToClipboard,
  createNotification,
  exportConfiguration,
  featureInfoRequest,
  getAppUrl,
  getProjectionConfig,
  trans,
} from "../../lib/utils";
import { createStringXY } from "ol/coordinate";
import { measureInteraction } from "./measurementsUtil";
import BaseLayerSwitcher from "./baseLayers";
import { Fill, Stroke, Style } from "ol/style.js";
import OLCesium from "olcs/OLCesium.js";
import ImageLayer from "ol/layer/Image";
import { ImageWMS } from "ol/source";
import Quicksearch from "../quicksearch/quicksearch";
import StreetView, {
  Options,
  i18n,
  SVEventTypes,
  Language,
  BtnControlSize,
  MapSize,
} from "ol-street-view";
import "ol-street-view/lib/style/css/ol-street-view.css";

proj4.defs(
  "EPSG:2100",
  "+proj=tmerc +lat_0=0 +lon_0=24 +k=0.9996 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=-199.87,74.79,246.62,0,0,0,0 +units=m +no_defs"
);

proj4.defs(
  "urn:x-ogc:def:crs:EPSG:2100",
  "+proj=tmerc +lat_0=0 +lon_0=24 +k=0.9996 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=-199.87,74.79,246.62,0,0,0,0 +units=m +no_defs"
);

proj4.defs(
  "http://www.opengis.net/gml/srs/epsg.xml#2100",
  "+proj=tmerc +lat_0=0 +lon_0=24 +k=0.9996 +x_0=500000 +y_0=0 +ellps=GRS80 +towgs84=-199.87,74.79,246.62,0,0,0,0 +units=m +no_defs"
);

proj4.defs(
  "EPSG:900913",
  "+title= Google Mercator EPSG:900913 +proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +no_defs"
);

proj4.defs(
  "urn:ogc:def:crs:EPSG::900913",
  "+title= Google Mercator EPSG:900913 +proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +no_defs"
);

proj4.defs(
  "EPSG:3857",
  "+proj=merc +a=6378137 +b=6378137 +lat_ts=0.0 +lon_0=0.0 +x_0=0.0 +y_0=0 +k=1.0 +units=m +nadgrids=@null +no_defs"
);

proj4.defs(
  "CRS:84",
  "+title=WGS 84 (long/lat) +proj=longlat +ellps=WGS84 +datum=WGS84 +units=degrees"
);

register(proj4);

function MapComponent(props) {
  const layerTree = useSelector((state) => store.getState().layerTree);
  const registeredLayerTree = useSelector(
    (state) => store.getState().registeredLayerTree.tree
  );

  const bookmarks = useSelector((state) => store.getState().map.bookmarks);

  const layers = useSelector((state) => store.getState().layers);
  const sources = useSelector((state) => store.getState().sources);
  const mapSettings = useSelector((state) => store.getState().map.settings);
  const [mapCenter, setMapCenter] = useState(null);

  const drawSource = new VectorSource({ wrapX: false });

  const drawLayer = new VectorLayer({
    source: drawSource,
    id: "drawLayer",
  });

  const editSource = new VectorSource({ wrapX: false });

  const editLayer = new VectorLayer({
    source: editSource,
    id: "editLayer",
  });

  const printSource = new VectorSource({ wrapX: false });

  const printLayer = new VectorLayer({
    source: printSource,
    id: "printLayer",
  });

  const measurementsSource = new VectorSource({ wrapX: false });

  const measurementLayer = new VectorLayer({
    source: measurementsSource,
    id: "measurementLayer",
  });

  const mapRef = useRef();
  const mousePositionRef = useRef();

  useEffect(() => {
    if (!window["map"]) {
      let viewConfig = MapViewConfig();

      window["map"] = new OLMap({
        target: mapRef.current,
        layers: [
          // new TileLayer({
          //   source: new OSM({ singleTile: true, ratio: 1 }),
          //   title: "OSM Topo",
          // }),

          editLayer,
          drawLayer,
          measurementLayer,
          printLayer,
        ],
        view: new View(viewConfig),
        controls: [new Zoom()],
      });

      window["ol3d"] = new OLCesium({
        map: window["map"],
      });

      if (window["appConfig"]()["cesiumIonToken"]) {
        window["Cesium"].Ion.defaultAccessToken =
          window["appConfig"]()["cesiumIonToken"];

        window["ol3d"]
          .getCesiumScene()
          .primitives.add(window["Cesium"].createOsmBuildings());

        window["Cesium"]
          .createWorldTerrainAsync()
          .then((tp) => (window["ol3d"].getCesiumScene().terrainProvider = tp));
      }

      window["editDraw"] = null;
      window["editSelect"] = null;
      window["editSource"] = editSource;
      window["editLayer"] = editLayer;
      window["drawSource"] = drawSource;
      window["drawLayer"] = drawLayer;

      window["printSource"] = printSource;
      window["printLayer"] = printLayer;

      window["measurementsSource"] = measurementsSource;
      window["measurementLayer"] = measurementLayer;
      mapScaleLine(mapSettings.scale);

      const mousePositionControl = new MousePosition({
        coordinateFormat: createStringXY(
          getProjectionConfig(mapSettings.projection).numDigits
        ),
        projection: mapSettings.projection,
        className: "d-inline-block",
        target: mousePositionRef.current,
        controlId: "mouseposition",
      });
      mousePositionControl.controlId = "mouseposition";

      window["map"].addControl(mousePositionControl);

      if (window["appConfig"]()["googleApiKey"]) {
        let streetView = new StreetView({
          apiKey: window["appConfig"]()["googleApiKey"],
          size: "sm",
        });

        streetView.controlId = "streetview";
        window["map"].addControl(streetView);
      }

      setMapCenter(getMapCenterProjected(mapSettings.projection));
      window["map"].on("moveend", () => {
        setMapCenter(getMapCenterProjected(mapSettings.projection));
      });
    } else {
      Object.keys(layers)?.forEach((el) => {
        if (!layers[el].mapLayer) {
          let mapLayer = addMapLayer(layers[el], sources[layers[el].source.id]);

          if (mapLayer) {
            let id = layers[el].id;

            //  mapLayer.setVisible();
            store.dispatch({
              type: "layer/loaded",
              payload: {
                id: id,
                mapLayer: mapLayer.ol_uid,
                layer: layers[el],
              },
            });
          }
        }
      });
      sortMapLayers(layerTree);

      mapRemoveControl("contextmenu");

      const contextmenu = new window["ContextMenu"]({
        width: 170,
        defaultItems: true, // defaultItems are (for now) Zoom In/Zoom Out
        items: [
          {
            text: "Feature info",
            callback: (e, map) => {
              getFeatureInfo(e, layers, sources, store);
            }, // `center` is your callback function
          },
          {
            text: "Get point",
            callback: (e, map) => {
              onGetPoint({
                coordinate: e.coordinate,
                pixel: window["map"].getPixelFromCoordinate(e.coordinate),
              });
            }, // `center` is your callback function
          },
          "-", // this is a separator
        ],
      });

      contextmenu.controlId = "contextmenu";
      window["map"].addControl(contextmenu);
    }
  }, [layers]);

  function onFeatureInfo(e) {
    getFeatureInfo(e, layers, sources, store);

    window["map"].un("singleclick", onFeatureInfo);
  }

  function onGetPoint(e) {
    let point = {
      projection: window["map"].getView().getProjection().getCode(),
      event: { coordinate: e.coordinate, pixel: e.pixel },
      label: "Map point ",
    };

    store.dispatch({ type: "map/mapGetPoints/add", payload: { ...point } });

    store.dispatch({
      type: "app/leftPanelActiveTab",
      payload: "mapGetPoints",
    });

    window["map"].un("singleclick", onGetPoint);
  }

  return (
    <div className="w-100 h-100">
      <Quicksearch></Quicksearch>
      <div
        ref={mapRef}
        className="map-wrapper"
        style={{
          width: "100%",
          height: "calc(100%)",
        }}
      >
        <div
          className="map-controls d-flex text-black"
          style={{ flexFlow: "column" }}
        >
          <span
            className="map-controls-icons text-center position-relative"
            role="button"
            onClick={() => {}}
            title="Baselayer"
          >
            <i
              className="fas fa-map fa-2xl "
              role="button"
              data-bs-toggle="collapse"
              data-bs-target="#map-baselayers-wrapper"
              onClick={() => {}}
            ></i>
            <div
              className="map-controls-baselayers-wrapper collapse p-3 pt-1 border shadow "
              id="map-baselayers-wrapper"
            >
              <BaseLayerSwitcher></BaseLayerSwitcher>
            </div>
          </span>

          <span
            className={"map-controls-icons text-center "}
            role="button"
            style={{ position: "relative" }}
            onClick={(e) => {
              window["map"].on("singleclick", onFeatureInfo);
            }}
          >
            <i className="fas fa-info fa-2xl "></i>
          </span>
          <span
            className="map-controls-icons text-center"
            role="button"
            style={{ position: "relative" }}
            data-bs-toggle="collapse"
            data-bs-target={"#layer-function-wrapper-id"}
            onClick={(e) => {
              store.dispatch({
                type: "app/leftPanelActiveTab",
                payload: "mapSetPoints",
              });
            }}
          >
            <i className="fas fa-map-marker-alt fa-2xl "></i>
          </span>
          <span
            className={"map-controls-icons text-center "}
            role="button"
            style={{ position: "relative" }}
            data-bs-toggle="collapse"
            data-bs-target={"#layer-function-wrapper-id"}
            onClick={(e) => {
              store.dispatch({
                type: "app/leftPanelActiveTab",
                payload: "mapGetPoints",
              });
              window["map"].on("singleclick", onGetPoint);
            }}
          >
            <i className="fas fa-map-marked-alt fa-2xl "></i>
          </span>
          <span
            className={"map-controls-icons text-center "}
            role="button"
            style={{ position: "relative" }}
          >
            <i
              className="fas fa-ruler-horizontal fa-2xl "
              data-bs-toggle="collapse"
              data-bs-target="#map-measurement-wrapper"
            ></i>
            <div
              className="map-controls-measurement-wrapper collapse  shadow "
              id="map-measurement-wrapper"
            >
              <div className="w-100">
                <span
                  className="map-controls-measurement-box m-1 p-1 text-center"
                  role="button"
                  onClick={(e) => {
                    measureInteraction("LineString");
                  }}
                >
                  <i className="fas fa-ruler-horizontal "></i>
                </span>
                <span
                  className="map-controls-measurement-box m-1 p-1 text-center"
                  role="button"
                  onClick={(e) => {
                    measureInteraction("Polygon");
                  }}
                >
                  <i className="fas fa-vector-square"></i>
                </span>
                <span
                  className="map-controls-measurement-box m-1 p-1 text-center"
                  role="button"
                  onClick={(e) => {
                    store.dispatch({ type: "map/measurements/clearall" });
                    window["measurementsSource"].clear();
                  }}
                >
                  <i className="fas fa-eraser"></i>
                </span>
              </div>
            </div>
          </span>
          <span
            className="map-controls-icons text-center"
            role="button"
            style={{ position: "relative" }}
            onClick={(e) => {
              window["primaryModal"].setState({
                show: true,
                size: "large",
                title: (
                  <>
                    <i className="fas fa-share-alt me-2 "></i>
                    {trans("Share map")}
                  </>
                ),
                body: (
                  <div className="row">
                    <div className="col-6">
                      <div
                        className="card m-1 "
                        style={{ minHeight: "120px" }}
                        role="button"
                        onClick={() => {
                          exportConfiguration(
                            layers,
                            layerTree,
                            registeredLayerTree,
                            window["map"],
                            bookmarks
                          );
                        }}
                      >
                        <div className="card-body align-items-center d-flex justify-content-center  rounded">
                          <div className="text-center h-100">
                            <div className="m-auto h5">
                              {trans("Donwload Configuration")}
                            </div>
                            <div className="mt-2 small">
                              {trans("Export all layers and map configuration")}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="col-6">
                      <div
                        className="card m-1 "
                        style={{ minHeight: "120px" }}
                        role="button"
                        onClick={() => {
                          copyToClipboard(getAppUrl());

                          createNotification({
                            type: "success",
                            message: trans("Link copied"),
                          });
                        }}
                      >
                        <div className="card-body align-items-center d-flex justify-content-center  rounded">
                          <div className="text-center h-100">
                            <div className="m-auto h5">
                              <br></br>
                              {trans("Copy link")}
                              <br></br>
                            </div>
                            <div className="mt-2 small">
                              {trans(
                                "Copy map url (only center and zoom level)"
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ),
                footer: [
                  <button
                    className="btn btn-sm btn-link"
                    onClick={(e) => {
                      window["primaryModal"].setState({
                        show: false,
                      });
                    }}
                  >
                    {trans("Cancel")}
                  </button>,
                ],
              });
            }}
          >
            <i className="fas fa-share-alt fa-2xl "></i>
          </span>
          <span
            className="map-controls-icons text-center"
            role="button"
            style={{ position: "relative" }}
            onClick={(e) => {
              if (window["ol3d"].getEnabled()) {
                window["ol3d"].setEnabled(false);
              } else {
                setTimeout(() => {
                  window["ol3d"].setEnabled(true);

                  var scene = window["ol3d"].getCesiumScene();
                }, 10);
              }
            }}
          >
            <strong>3D</strong>
          </span>
        </div>
      </div>

      <div
        className="d-block me-2"
        style={{ height: "32px", overflowX: "auto" }}
      >
        <span className="small me-2">
          <small>
            {trans("Center")}: {mapCenter?.join(" ")}
          </small>
        </span>
        <small>{"|"}</small>
        <span className="small me-2 ms-2">
          <small>
            {trans("Projection")}:{" "}
            {getProjectionConfig(mapSettings.projection)?.title}
          </small>
        </span>{" "}
        <small>{"|"}</small>
        <span className="small me-2 ms-2">
          <small>
            {" "}
            {trans("Mouse")}: <span ref={mousePositionRef}></span>
          </small>
        </span>
      </div>
    </div>
  );
}

export default MapComponent;
